/** Payment Account status + capabilities + USIO Click-to-Agree (USIO's own agreement, embedded). */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { StatusPill, btnGhost, btnPrimary } from '../finance/financeUi';
import { CAPABILITY_LABELS, STATUS_COPY, errorDetail, paApi } from './paymentAccount';

export function ClickToAgree({ onDone }) {
  const [cta, setCta] = useState(null);
  useEffect(() => { paApi.clickToAgree().then(setCta).catch((e) => toast.error(errorDetail(e))); }, []);
  useEffect(() => {
    if (!cta) return undefined;
    const listener = async (event) => {
      if (event.origin !== cta.origin || event.data !== cta.completion_message) return;
      try { onDone(await paApi.agreed()); toast.success('Provider agreement completed'); } catch (e) { toast.error(errorDetail(e)); }
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [cta, onDone]);
  if (!cta) return null;
  return (
    <div className="space-y-2" data-testid="pa-click-to-agree">
      <p className="text-sm text-muted-foreground">Review and accept the payment provider's own agreement below. It is separate from your MSC paperwork.</p>
      <iframe title="Provider agreement" src={cta.url} className="h-[560px] w-full rounded-lg border bg-white" data-testid="pa-click-to-agree-frame" />
      <button type="button" className={btnGhost} onClick={async () => { try { onDone(await paApi.agreed()); } catch (e) { toast.error(errorDetail(e)); } }} data-testid="pa-cta-check-btn">I've finished — check status</button>
    </div>
  );
}

export function Capabilities({ capabilities }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="pa-capabilities">
      {Object.entries(CAPABILITY_LABELS).map(([k, label]) => (
        <div key={k} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
          <span>{label}</span><StatusPill value={(capabilities?.[k] || {}).status || 'NOT_REQUESTED'} testid={`pa-cap-${k}`} />
        </div>
      ))}
    </div>
  );
}

export function AccountStatus({ account, program, onChange }) {
  const refresh = async () => { try { const r = await paApi.refresh(); onChange(r.account); } catch (e) { toast.error(errorDetail(e)); } };
  const r = account.readiness || {};
  return (
    <div className="space-y-4" data-testid="pa-account-status">
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {[['Provider', account.provider_name], ['Program sponsor', account.program_sponsor], ['Account type', 'Submerchant'], ['Environment', account.environment]].map(([k, v]) => (
          <div key={k} className="flex justify-between border-b py-1"><dt className="text-muted-foreground">{k}</dt><dd data-testid={`pa-${k.toLowerCase().replace(/ /g, '-')}`}>{v}</dd></div>
        ))}
      </dl>
      <div className="flex flex-wrap items-center gap-3">
        <StatusPill value={account.canonical_status} testid="pa-status" />
        <span className="text-sm text-muted-foreground" data-testid="pa-status-copy">{STATUS_COPY[account.canonical_status]}</span>
        <button type="button" className={btnGhost} onClick={refresh} data-testid="pa-refresh-btn">Refresh status</button>
      </div>
      {program?.development_sandbox && <p className="rounded-md border border-dashed border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="pa-dev-sandbox-banner">Development sandbox (provider's public test account) — not MSC's certified program. No real account is created.</p>}
      {account.canonical_status === 'ACTION_REQUIRED' && account.external_guid && <ClickToAgree onDone={onChange} />}
      {account.provider_status_raw === 'PENDING_MANUAL_PROVISIONING' && <p className="text-sm" data-testid="pa-manual-note">MSC is setting up your {account.provider_name} account. You'll see the status change here.</p>}
      <Capabilities capabilities={account.capabilities} />
      <p className="text-sm" data-testid="pa-readiness">
        {r.downline_payout_ready ? <span className="font-medium text-emerald-700">Payment Account Ready — sending ACH from ISOCNTRL arrives in a later release.</span>
          : 'Downline payouts through ISOCNTRL need an active account, active ACH credits, a verified funding bank and your origination authorization.'}
      </p>
      {account.canonical_status === 'FAILED' && <button type="button" className={btnPrimary} onClick={() => onChange(null, true)} data-testid="pa-retry-btn">Continue setup</button>}
    </div>
  );
}
